import type { AuditLogFilters } from "~/lib/queries";

/**
 * Every filter on the audit log, as URL search params.
 *
 * Same contract as the other lists: the URL is the only place these live, so a
 * filtered view — "what happened to this merchant", "what did this person do
 * today" — is a link someone can paste into an incident thread.
 */
export interface AuditLogSearch {
  actor_id?: string;
  action?: string;
  target_type?: string;
  target_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

const FILTER_KEYS = [
  "actor_id",
  "action",
  "target_type",
  "target_id",
  "start_date",
  "end_date",
] as const satisfies readonly (keyof AuditLogSearch)[];

export function validateAuditLogSearch(raw: Record<string, unknown>): AuditLogSearch {
  return {
    actor_id: str(raw.actor_id),
    action: str(raw.action),
    target_type: str(raw.target_type),
    target_id: str(raw.target_id),
    start_date: str(raw.start_date),
    end_date: str(raw.end_date),
    limit: num(raw.limit),
    offset: num(raw.offset),
  };
}

export function hasActiveFilters(search: AuditLogSearch): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined);
}

export function toFilters(search: AuditLogSearch): AuditLogFilters {
  return {
    actor_id: search.actor_id,
    action: search.action,
    target_type: search.target_type,
    target_id: search.target_id,
    start_date: search.start_date,
    end_date: search.end_date,
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  };
}

function str(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function num(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(str(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}
